import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import styles from './styles.module.css';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { AlertMessagesContext } from 'react-alert-messages';
import { useParams, useHistory } from 'react-router-dom';
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  InputAdornment,
  IconButton as MuiIconButton,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CartHelper } from 'hooks/CartHelper';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import ShopsHelper from 'helpers/ShopsHelper';
import TerminalService from 'services/TerminalServices';
import { useTranslation } from 'react-i18next';
import StockTransferService from 'services/StockTransferService';
import { StockTransferHelper } from 'hooks/StockTransferHelper';
import { ROUTES } from 'const';
import BatchSelectionPopup from 'components/popups/BatchSelectionPopup';
import ItemService from 'services/ItemService';
import { selectedTabContext } from 'Context/SelectedTabContext';
import NumberField from 'components/library/NumberField';

const TRANSFER_STATUS = {
  DRAFT: 'DRAFT',
  TRANSIT: 'TRANSIT',
  RECEIVED: 'RECEIVED',
  REJECTED: 'REJECTED',
};

let initialFetchPromise = null;
let unmountTimeout = null;

function NewInternalStockTransfer() {
  const history = useHistory();
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { t: tPopup } = useTranslation('popup');

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts, stockBatches, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, warehouseProducts, getWarehouseProducts } = useContext(WarehouseContext);
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);

  const { id } = useParams();
  const { shop } = useContext(ShopContext);
  const CartManager = CartHelper();
  const StockTransferManager = StockTransferHelper();

  const [transferItems, setTransferItems] = useState([]);
  const [rate, setRate] = useState('');
  const [stock, setStock] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [cess, setCess] = useState(0);
  const [item, setItem] = useState(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [editData, setEditData] = useState({});
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [transferId] = useState(id && id !== 'new' ? id : null);
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [remark, setRemark] = useState('');
  const [profitPercentage] = useState(0);
  const [selectedItemBatches, setSelectedItemBatches] = useState([]);
  const [batchPopupAnchor, setBatchPopupAnchor] = useState(null);
  const [localBatches, setLocalBatches] = useState([]);
  const [editingTableItemIndex, setEditingTableItemIndex] = useState(null);
  const [originalBatchQtys, setOriginalBatchQtys] = useState({});
  const [subItemBatchPopup, setSubItemBatchPopup] = useState({
    open: false,
    anchorEl: null,
    item: null,
    subItem: null,
    saleItemIndex: null,
  });
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [batchName, setBatchName] = useState(ShopsHelper.generateBatchName());

  const filteredProductsList = useMemo(() => {
    return products || [];
  }, [products]);

  const filteredStockBatches = useMemo(() => {
    if (!fromWarehouseId) return localBatches || [];
    return localBatches.filter((b) => {
      const bWhId = b.warehouse?.id || b.warehouse || null;
      return String(bWhId) === String(fromWarehouseId);
    });
  }, [fromWarehouseId, localBatches]);

  const dltItem = (item) => {
    setTransferItems(transferItems.filter(({ item_id, rate }) => item_id !== item.item_id || rate !== item.rate));
  };

  const resetItemFields = () => {
    setRate('');
    setQuantity('');
    setTax('');
    setCess('');
    setTotalAmount('');
    setItem(null);
    setSelectedItemBatches([]);
    setBatchPopupAnchor(null);
  };

  const addItem = () => {
    if (quantity > stock) {
      postAlertMessage({
        text: `${tOperations('Quantity cannot exceed available stock')} (${stock})`,
        type: 'failed',
      });
      return;
    }
    let sub_item = [];
    if (item && item.type === 'COMBO') {
      sub_item = (item.details?.sub_items || []).map((subItem) => {
        const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
        const requiredQty = subItem.fraction * quantity;

        let selectedBatches = [];
        if (prod?.is_batch_enabled) {
          const availableBatches = (stockBatches || [])
            .filter((b) => {
              const isSameItem = String(b.item) === String(subItem.item_id);
              if (!isSameItem) return false;
              const expiryDate = b.expiry_date ? new Date(b.expiry_date.replace('Z', '')) : null;
              const isExpired = expiryDate && expiryDate <= new Date();
              return !isExpired;
            })
            .sort((a, b) => {
              if (!a.expiry_date) return 1;
              if (!b.expiry_date) return -1;
              return new Date(a.expiry_date) - new Date(b.expiry_date);
            });

          let remainingToAllocate = requiredQty;
          for (const batch of availableBatches) {
            if (remainingToAllocate <= 0) break;
            const take = Math.min(remainingToAllocate, batch.stock || 0);
            if (take > 0) {
              selectedBatches.push({
                batch_id: batch.id,
                quantity: take,
                batch_no: batch.batch_no || batch.batch_name,
                expiry_date: batch.expiry_date,
              });
              remainingToAllocate -= take;
            }
          }
        }

        const subItemRate = StockTransferManager.getStockTransferItemUnitRate(prod, profitPercentage);
        const subItemTaxPercent = prod?.tax || 0;
        const subItemCessPercent = prod?.cess || 0;

        const tempSubItem = {
          quantity: requiredQty,
          rate: subItemRate,
          tax: subItemTaxPercent,
          cess: subItemCessPercent,
        };

        const subItemCess = StockTransferManager.getItemCess(tempSubItem);
        const subItemTax = StockTransferManager.getItemTax(tempSubItem);

        return {
          id: subItem.id,
          item_id: subItem.item_id,
          name: subItem.name || prod?.name || '-',
          quantity: requiredQty,
          fraction: subItem.fraction,
          cost: prod?.cost || 0,
          totalCost: requiredQty * (prod?.cost || 0),
          is_batch_enabled: prod?.is_batch_enabled,
          batch_data: selectedBatches,
          rate: subItemRate,
          tax: subItemTax,
          cess: subItemCess,
          payable: subItemRate * requiredQty + subItemTax + subItemCess,
          plu: prod?.plu || '',
          price: prod?.price || 0,
          tax_percent: subItemTaxPercent,
          cess_percent: subItemCessPercent,
        };
      });
    }

    const data = {
      item_id: item ? item.id : null,
      plu: item ? item.plu : null,
      rate,
      quantity,
      tax: tax === '' ? 0 : tax ?? 0,
      cess: cess === '' ? 0 : cess ?? 0,
      payable: totalAmount,
      name: item ? item.name : '',
      item,
      cost: item ? item.cost : 0,
      batch_data: selectedItemBatches,
      sub_item,
    };

    setTransferItems([...transferItems, data]);
    resetItemFields();
  };

  const getTerminals = async () => {
    const resp = await TerminalService.getTerminals();
    setTerminals(resp);
    const defaultTerminal = resp.find((t) => t.auth_token === null);
    setSelectedTerminal(defaultTerminal);
  };

  const fetchInitialData = async () => {
    if (unmountTimeout) {
      clearTimeout(unmountTimeout);
      unmountTimeout = null;
    }
    if (initialFetchPromise) {
      await initialFetchPromise;
      return;
    }

    initialFetchPromise = (async () => {
      setLoadingIndicator(true);
      try {
        const promises = [getTerminals(), getProducts(), getStockBatches()];
        if (ShopsHelper.isWarehouseFeatureActive(shop)) {
          promises.push(getWarehouses({ useCache: true }));
          promises.push(getWarehouseProducts({ useCache: true }));
        }
        await Promise.all(promises);
      } finally {
        setLoadingIndicator(false);
      }
    })();

    await initialFetchPromise;
  };

  const getInvalidComboItem = () => {
    if (transferItems.length === 0) return null;

    const invalidItem = transferItems.find(
      (transferItem) =>
        transferItem.item?.type === 'COMBO' &&
        (transferItem.sub_item || []).some((si) => {
          if (!si.is_batch_enabled) return false;
          const totalSelected = (si.batch_data || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
          return Math.abs(si.quantity - totalSelected) >= 0.001;
        })
    );
    return invalidItem;
  };

  const handleupdateStockTransfer = async (status) => {
    if (!fromWarehouseId || !toWarehouseId) {
      postAlertMessage({ text: tCommon('Source and destination warehouses are required'), type: 'failed' });
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      postAlertMessage({ text: tCommon('Source and destination warehouses cannot be the same'), type: 'failed' });
      return;
    }
    if (transferItems.length === 0) {
      postAlertMessage({ text: tOperations('Please add at least one item'), type: 'failed' });
      return;
    }
    if (item) {
      postAlertMessage({
        text: tOperations('Please add the selected item to the list before saving'),
        type: 'failed',
      });
      return;
    }

    const invalidCombo = getInvalidComboItem();
    if (invalidCombo) {
      postAlertMessage({
        text: `Total batch quantity must match the required quantity for sub items in "${invalidCombo.name}".`,
        type: 'failed',
      });
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        from_warehouse_id: fromWarehouseId,
        to_warehouse_id: toWarehouseId,
        status,
        data: {
          items: transferItems,
          payable: transferTotal?.payable,
          tax: transferTotal?.tax,
          cess: transferTotal?.cess,
          remark,
          terminal: selectedTerminal?.id || null,
          batch_name: batchName,
        },
      };

      await StockTransferService.updateInternalStockTransfer(transferId, data);
      if (status === TRANSFER_STATUS.DRAFT) {
        history.push(ROUTES.INTERNAL_STOCK_TRANSFER);
        return;
      }
      await getProducts({ useCache: false });
      const hasBatchItems = transferItems.some(
        (item) =>
          (item.batch_data && item.batch_data.length > 0) ||
          (item.item?.type === 'COMBO' && (item.sub_item || []).some((si) => si.batch_data && si.batch_data.length > 0))
      );
      if (hasBatchItems) {
        await getStockBatches({ useCache: false });
      }
      if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        await getWarehouseProducts({ useCache: false });
      }
      history.push(ROUTES.INTERNAL_STOCK_TRANSFER);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleAddStockTransfer = async (status) => {
    if (!fromWarehouseId || !toWarehouseId) {
      postAlertMessage({ text: tCommon('Source and destination warehouses are required'), type: 'failed' });
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      postAlertMessage({ text: tCommon('Source and destination warehouses cannot be the same'), type: 'failed' });
      return;
    }
    if (transferItems.length === 0) {
      postAlertMessage({ text: tOperations('Please add at least one item'), type: 'failed' });
      return;
    }
    if (item) {
      postAlertMessage({
        text: tOperations('Please add the selected item to the list before saving'),
        type: 'failed',
      });
      return;
    }

    const invalidCombo = getInvalidComboItem();
    if (invalidCombo) {
      postAlertMessage({
        text: `Total batch quantity must match the required quantity for sub items in "${invalidCombo.name}".`,
        type: 'failed',
      });
      return;
    }

    setLoadingIndicator(true);

    try {
      const transferData = {
        from_warehouse_id: fromWarehouseId,
        to_warehouse_id: toWarehouseId,
        status,
        data: {
          items: transferItems,
          payable: transferTotal?.payable,
          tax: transferTotal?.tax,
          cess: transferTotal?.cess,
          remark,
          terminal: selectedTerminal?.id || null,
          batch_name: batchName,
        },
      };

      await StockTransferService.createInternalStockTransfer(transferData);
      if (status === TRANSFER_STATUS.DRAFT) {
        postAlertMessage({
          text: tPopup('Stock transfered successfully'),
          type: 'success',
        });
        history.push(ROUTES.INTERNAL_STOCK_TRANSFER);
        return;
      }
      await getProducts({ useCache: false });
      const hasBatchItems = transferItems.some(
        (item) =>
          (item.stock_batch && item.stock_batch.length > 0) ||
          (item.batch_data && item.batch_data.length > 0) ||
          (item.item?.type === 'COMBO' && (item.sub_item || []).some((si) => si.batch_data && si.batch_data.length > 0))
      );
      if (hasBatchItems) {
        await getStockBatches({ useCache: false });
      }
      if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        await getWarehouseProducts({ useCache: false });
      }
      postAlertMessage({
        text: tPopup('Stock transfered successfully'),
        type: 'success',
      });
      history.push(ROUTES.INTERNAL_STOCK_TRANSFER);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleDeleteStockTransfer = async () => {
    if (window.confirm(tCommon('Are you sure you want to delete this stock transfer?'))) {
      setLoadingIndicator(true);
      try {
        await StockTransferService.deleteInternalStockTransfer(transferId);
        postAlertMessage({
          text: tCommon('Internal stock transfer deleted successfully'),
          type: 'success',
        });
        history.push(ROUTES.INTERNAL_STOCK_TRANSFER);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      } finally {
        setLoadingIndicator(false);
      }
    }
  };

  const getItemAvailableStock = (productItem) => {
    if (!productItem || !productItem.id) return 0;
    if (fromWarehouseId && Array.isArray(warehouseProducts)) {
      const wp = warehouseProducts.find((p) => {
        const bWhId = p.warehouse?.id || p.warehouse || null;
        const bItemId = p.item?.id || p.item || null;
        return String(bWhId) === String(fromWarehouseId) && String(bItemId) === String(productItem.id);
      });
      return wp ? wp.stock : 0;
    }
    return productItem.stock || 0;
  };

  const selectItemHandler = (item) => {
    const initialStock = getItemAvailableStock(item);
    let defaultQty = item.is_batch_enabled ? 0 : 1;
    if (shop?.stock_availability_check_for_sale) {
      defaultQty = Math.max(0, Math.min(defaultQty, initialStock));
    }

    const selectedItem = {
      ...item,
      quantity: defaultQty,
      cost: item.cost,
      rate: StockTransferManager.getStockTransferItemUnitRate(item, profitPercentage),
    };
    setItem(selectedItem || '');

    setStock(initialStock ?? 0);
    setRate(selectedItem?.rate || 0);
    setQuantity(defaultQty);
    let itemTax = StockTransferManager.getItemTax(selectedItem);
    let itemCess = StockTransferManager.getItemCess(selectedItem);
    setTax(itemTax);
    setCess(itemCess);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, CartManager.getItemAmount(selectedItem) + itemTax + itemCess));
    setSelectedItemBatches([]);
  };

  const handleBatchQuantityChange = (batchId, val) => {
    let qty = Number(val);
    const batch = (localBatches || []).find((b) => b.id === batchId);
    const adjustedStock = (batch?.stock || 0) + (originalBatchQtys[String(batchId)] || 0);

    if (batch && qty > adjustedStock) {
      qty = adjustedStock;
      postAlertMessage({
        text: `${tOperations('Quantity cannot exceed available stock')} (${adjustedStock})`,
        type: 'failed',
      });
    }

    const updateBatchList = (prev, bId, bQty) => {
      if (bQty <= 0) {
        return prev.filter((b) => b.batch_id !== bId);
      }
      const existing = prev.find((b) => b.batch_id === bId);
      if (existing) {
        return prev.map((b) => (b.batch_id === bId ? { ...b, quantity: bQty } : b));
      } else {
        return [...prev, { batch_id: bId, quantity: bQty, expiry_date: batch?.expiry_date }];
      }
    };

    if (editingTableItemIndex !== null) {
      setTransferItems((prev) =>
        prev.map((si, idx) => {
          if (idx === editingTableItemIndex) {
            const updatedBatches = updateBatchList(si.batch_data || [], batchId, qty);
            const totalQty = updatedBatches.reduce((sum, b) => sum + b.quantity, 0);
            const updatedItem = {
              ...si,
              batch_data: updatedBatches,
              quantity: totalQty,
              tax: si.item?.tax || 0,
              cess: si.item?.cess || 0,
            };
            updatedItem.tax = StockTransferManager.getItemTax(updatedItem);
            updatedItem.cess = StockTransferManager.getItemCess(updatedItem);
            updatedItem.payable = CartManager.getItemAmount(updatedItem) + updatedItem.tax + updatedItem.cess;
            return updatedItem;
          }
          return si;
        })
      );
    } else {
      setSelectedItemBatches((prev) => {
        const newBatches = updateBatchList(prev, batchId, qty);
        const totalBatchQty = newBatches.reduce((sum, b) => sum + b.quantity, 0);
        handleChangeQty(totalBatchQty);
        return newBatches;
      });
    }
  };

  const handleOpenSubItemBatchPopup = (event, saleItemIndex, subItem) => {
    const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
    setSubItemBatchPopup({
      open: true,
      anchorEl: event.currentTarget,
      item: prod,
      subItem: subItem,
      saleItemIndex: saleItemIndex,
    });
  };

  const handleSubItemBatchChange = (batchId, val) => {
    const { saleItemIndex, subItem } = subItemBatchPopup;
    const qtyValue = parseFloat(val) || 0;

    const batch = (localBatches || []).find((b) => String(b.id) === String(batchId));
    const origQty = originalBatchQtys[String(batchId)] || 0;
    const availableStock = (batch?.stock || 0) + origQty;

    let finalQty = qtyValue;
    if (finalQty > availableStock) {
      postAlertMessage({
        text: `${tOperations('Quantity cannot exceed available stock')} (${availableStock})`,
        type: 'failed',
      });
      finalQty = availableStock;
    }

    setTransferItems((prev) => {
      const next = [...prev];
      const targetSaleItem = { ...next[saleItemIndex] };
      const nextSubItemArray = (targetSaleItem.sub_item || []).map((si) => ({ ...si }));

      const subItemIndex = nextSubItemArray.findIndex((si) => si.item_id === subItem.item_id);
      if (subItemIndex === -1) return prev;

      const updatedSubItem = { ...nextSubItemArray[subItemIndex] };
      const newBatches = [...(updatedSubItem.batch_data || [])];
      const batchIndex = newBatches.findIndex((b) => String(b.batch_id) === String(batchId));

      if (batchIndex > -1) {
        if (finalQty > 0) {
          newBatches[batchIndex] = { ...newBatches[batchIndex], quantity: finalQty };
        } else {
          newBatches.splice(batchIndex, 1);
        }
      } else if (finalQty > 0) {
        newBatches.push({
          batch_id: batchId,
          quantity: finalQty,
          batch_no: batch?.batch_no || batch?.batch_name || '-',
          expiry_date: batch?.expiry_date,
        });
      }

      updatedSubItem.batch_data = newBatches;
      nextSubItemArray[subItemIndex] = updatedSubItem;
      targetSaleItem.sub_item = nextSubItemArray;
      next[saleItemIndex] = targetSaleItem;

      setSubItemBatchPopup((prevPopup) => ({
        ...prevPopup,
        subItem: updatedSubItem,
      }));

      return next;
    });
  };

  const getItemRate = useCallback(
    (iRate = rate, iQty = quantity) => {
      return ShopsHelper.getAmountFormatted(shop, Number(iRate) * iQty);
    },
    [rate, quantity, shop]
  );

  const getTax = useCallback(
    (iRate = rate, iQty = quantity, iTax = item?.tax ?? 0) => {
      return ShopsHelper.getAmountFormatted(shop, (getItemRate(iRate, iQty) * iTax) / 100);
    },
    [item?.tax, getItemRate, quantity, rate, shop]
  );

  const getCess = useCallback(
    (iRate = rate, iQty = quantity, iCess = item?.cess ?? 0) => {
      return ShopsHelper.getAmountFormatted(shop, (getItemRate(Number(iRate), iQty) * iCess) / 100);
    },
    [item?.cess, getItemRate, quantity, rate, shop]
  );

  const handleChangeQty = (val) => {
    if (val === '') {
      setQuantity('');
      return;
    }
    let qty = Number(val);
    if (qty < 0) qty = 0;

    if (shop?.stock_availability_check_for_sale) {
      const availableStock = getItemAvailableStock(item);
      const limitStock = Math.max(0, availableStock);
      if (qty > limitStock) {
        postAlertMessage({
          text: tOperations('Insufficient stock. Available stock is {{stock}}', { stock: limitStock }),
          type: 'failed',
        });
        qty = limitStock;
      }
    }

    if (item === null) {
      setQuantity(qty);
      if (rate) {
        setTotalAmount(rate * qty);
      } else if (totalAmount) {
        setRate(totalAmount / qty);
      }
      return;
    }
    let itemTax = getTax(rate, qty);
    let itemCess = getCess(rate, qty);
    setTax(itemTax);
    setCess(itemCess);
    setQuantity(qty);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, getItemRate(rate, qty) + itemTax + itemCess));
  };

  useEffect(() => {
    setSelectedTab('stock-transfer');
    setSelectedSubTab(ROUTES.INTERNAL_STOCK_TRANSFER);
    setSections((prev) => ({ ...prev, operations: true, stockTransfer: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (transferItems.length > 0) {
      const updatedItems = CartManager.updateItemRateWithProfit([...transferItems], profitPercentage);
      setTransferItems(updatedItems);
    }
    //eslint-disable-next-line
  }, [profitPercentage]);

  const transferTotal = transferItems.reduce(
    (acc, item) => {
      acc.payable += Number(item.payable) || 0;
      acc.tax += Number(item.tax) || 0;
      acc.cess += Number(item.cess) || 0;
      return acc;
    },
    { payable: 0, tax: 0, cess: 0 }
  );

  useEffect(() => {
    fetchInitialData();
    return () => {
      unmountTimeout = setTimeout(() => {
        initialFetchPromise = null;
      }, 100);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (warehouses && warehouses.length > 0) {
      if (ShopsHelper.isWarehouseFeatureActive(shop)) {
        if (!transferId) {
          const defaultWhId = selectedTerminal?.default_warehouse || shop?.config?.default_warehouse;
          const defaultWh = warehouses.find((w) => w.id === defaultWhId);
          setFromWarehouseId(defaultWh ? defaultWh.id : warehouses[0].id);
        }
      }
    }
  }, [shop, selectedTerminal, transferId, warehouses]);

  useEffect(() => {
    if (stockBatches && stockBatches.length > 0) {
      setLocalBatches(stockBatches);
    }
  }, [stockBatches]);

  useEffect(() => {
    if (!transferId || terminals.length === 0) {
      return;
    }

    const loadStockTransfer = async () => {
      const resp = await StockTransferService.getInternalStockTransfer(transferId);
      setEditData(resp);
      const processedItems = (resp?.data?.items || []).map((si) => {
        let updatedItem = si?.item;
        if (updatedItem?.id) {
          const invItem = (products || []).find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }

        let sub_item = [];
        if (updatedItem?.type === 'COMBO') {
          let savedSubItemBatches = si.sub_item_batch_data || si.config?.sub_item_batch_data || [];
          if (savedSubItemBatches.length === 0) {
            const nestedSubItems = si.sub_item || si.config?.sub_item || [];
            nestedSubItems.forEach((subItemEntry) => {
              if (subItemEntry.batch_data) {
                subItemEntry.batch_data.forEach((b) => {
                  savedSubItemBatches.push({
                    item_id: subItemEntry.item_id,
                    batch_id: b.batch_id,
                    quantity: b.quantity,
                  });
                });
              }
            });
          }

          sub_item = (updatedItem.details?.sub_items || []).map((subItem) => {
            const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
            const requiredQty = subItem.fraction * si.quantity;

            const selectedBatches = savedSubItemBatches
              .filter((sb) => String(sb.item_id) === String(subItem.item_id))
              .map((sb) => {
                const batch =
                  (localBatches || []).find((b) => String(b.id) === String(sb.batch_id)) ||
                  (stockBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                return {
                  batch_id: sb.batch_id,
                  quantity: sb.quantity,
                  batch_no: batch?.batch_no || batch?.batch_name || '-',
                };
              });

            const subItemRate = StockTransferManager.getStockTransferItemUnitRate(prod, profitPercentage);
            const subItemTaxPercent = prod?.tax || 0;
            const subItemCessPercent = prod?.cess || 0;

            const tempSubItem = {
              quantity: requiredQty,
              rate: subItemRate,
              tax: subItemTaxPercent,
              cess: subItemCessPercent,
            };

            const subItemCess = StockTransferManager.getItemCess(tempSubItem);
            const subItemTax = StockTransferManager.getItemTax(tempSubItem);

            return {
              id: subItem.id,
              item_id: subItem.item_id,
              name: subItem.name || prod?.name || '-',
              quantity: requiredQty,
              fraction: subItem.fraction,
              cost: prod?.cost || 0,
              totalCost: requiredQty * (prod?.cost || 0),
              is_batch_enabled: prod?.is_batch_enabled,
              batch_data: selectedBatches,
              rate: subItemRate,
              tax: subItemTax,
              cess: subItemCess,
              payable: subItemRate * requiredQty + subItemTax + subItemCess,
              plu: prod?.plu || '',
              price: prod?.price || 0,
              tax_percent: subItemTaxPercent,
              cess_percent: subItemCessPercent,
            };
          });
        }

        return {
          ...si,
          item: updatedItem,
          item_id: updatedItem?.id,
          name: updatedItem?.name,
          plu: updatedItem?.plu || si.plu || '',
          batch_data: si.config?.batch_data || si.batch_data || [],
          sub_item,
        };
      });

      setTransferItems(processedItems);
      setSelectedTerminal(terminals.find((t) => t.id === resp?.data?.terminal) || null);
      setFromWarehouseId(resp?.from_warehouse?.id || '');
      setToWarehouseId(resp?.to_warehouse?.id || '');
      setRemark(resp?.data?.remark || '');
      setBatchName(resp?.data?.batch_name || '');

      const missingBatchIds = [];
      processedItems.forEach((item) => {
        if (item.batch_data) {
          item.batch_data.forEach((sb) => {
            if (!localBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
              missingBatchIds.push(sb.batch_id);
            }
          });
        }
        if (item.sub_item) {
          item.sub_item.forEach((si) => {
            if (si.batch_data) {
              si.batch_data.forEach((sb) => {
                if (!localBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
                  missingBatchIds.push(sb.batch_id);
                }
              });
            }
          });
        }
      });

      if (missingBatchIds.length > 0) {
        try {
          const fetchedBatches = await ItemService.getProductBatches({
            ids: missingBatchIds.join(','),
          });
          if (fetchedBatches && fetchedBatches.length > 0) {
            setLocalBatches((prev) => {
              const prevFiltered = prev.filter((b) => !fetchedBatches.some((fb) => fb.id === b.id));
              return [...prevFiltered, ...fetchedBatches];
            });
          }
        } catch (err) {
          postAlertMessage({ text: err.message, type: 'failed' });
        }
      }

      const origQtys = {};
      processedItems.forEach((item) => {
        if (item.batch_data) {
          item.batch_data.forEach((sb) => {
            origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + sb.quantity;
          });
        }
        if (item.sub_item) {
          item.sub_item.forEach((si) => {
            if (si.batch_data) {
              si.batch_data.forEach((sb) => {
                origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + sb.quantity;
              });
            }
          });
        }
      });
      setOriginalBatchQtys(origQtys);
    };

    loadStockTransfer();
    //eslint-disable-next-line
  }, [transferId, terminals]);

  const selectProductsRef = React.useRef();

  return (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tCommon('Internal Stock Transfer')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
          </h2>
        </div>
        <div className={styles.changeable}>
          <div className={styles.headTitle} style={{ paddingBottom: '16px' }}>
            <h2 className={styles.subTitle}>
              {editData.id ? tCommon('Edit Stock Transfer') : tCommon('New Stock Transfer')}
            </h2>
          </div>
          <div>
            <Grid container spacing={3} className={styles.inputRow}>
              <Grid item xs={12} sm={6} pt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="from-warehouse-select-label">{tCommon('From Warehouse')}</InputLabel>
                  <Select
                    size="small"
                    labelId="from-warehouse-select-label"
                    value={fromWarehouseId || ''}
                    onChange={(e) => {
                      const newFromWarehouseId = e.target.value === '' ? '' : Number(e.target.value);
                      if (newFromWarehouseId !== fromWarehouseId) {
                        setFromWarehouseId(newFromWarehouseId);
                        resetItemFields();
                        setTransferItems([]);
                        setOriginalBatchQtys({});
                      }
                    }}
                    label={tCommon('From Warehouse')}
                    style={{ height: 42 }}
                    disabled={transferId}
                  >
                    <MenuItem value="">
                      <em>{tCommon('None')}</em>
                    </MenuItem>
                    {(warehouses || []).map((wh) => (
                      <MenuItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} pt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="to-warehouse-select-label">{tCommon('To Warehouse')}</InputLabel>
                  <Select
                    size="small"
                    labelId="to-warehouse-select-label"
                    value={toWarehouseId || ''}
                    onChange={(e) => {
                      setToWarehouseId(e.target.value === '' ? '' : Number(e.target.value));
                    }}
                    label={tCommon('To Warehouse')}
                    style={{ height: 42 }}
                    disabled={transferId}
                  >
                    <MenuItem value="">
                      <em>{tCommon('None')}</em>
                    </MenuItem>
                    {(warehouses || []).map((wh) => (
                      <MenuItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {fromWarehouseId && toWarehouseId && (
              <>
                <Grid container spacing={3} className={styles.inputRow}>
                  <Grid item xs={12} sm={6}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Item')}</label>
                    </div>
                    <Autocomplete
                      id="item-select"
                      size="small"
                      openOnFocus
                      value={item}
                      onChange={(event, newValue) => {
                        if (newValue) selectItemHandler(newValue);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <span>{option.name}</span>
                            <span style={{ color: '#718096', fontSize: '12px' }}>(PLU: {option.plu})</span>
                          </Box>
                        </li>
                      )}
                      filterOptions={(options, { inputValue }) =>
                        options.filter(
                          (option) =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                            String(option.plu).toLowerCase().includes(inputValue.toLowerCase()) ||
                            (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                        )
                      }
                      options={filteredProductsList || []}
                      ListboxProps={{
                        sx: { overflowX: 'auto', maxHeight: '180px' },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={tCommon('Search by plu, name')}
                          inputRef={selectProductsRef}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Available Stock')}</label>
                    </div>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      value={stock}
                      style={{ width: '100%' }}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Quantity')}</label>
                    </div>
                    {item?.is_batch_enabled ? (
                      <NumberField
                        type="text"
                        value={quantity}
                        style={{ width: '100%', cursor: 'pointer' }}
                        sx={{ '& input': { cursor: 'pointer' } }}
                        inputProps={{ readOnly: true }}
                        onKeyDown={(e) => e.preventDefault()}
                        InputProps={{
                          onClick: (e) => {
                            setBatchPopupAnchor(e.currentTarget);
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <MuiIconButton color="primary" size="small">
                                <ArrowDropDownIcon fontSize="small" />
                              </MuiIconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <NumberField
                        value={quantity}
                        onChange={(e) => handleChangeQty(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>&nbsp;</label>
                    </div>
                    <IconButton color="primary" onClick={addItem} disabled={!item || quantity <= 0}>
                      <LibraryAddIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                {transferItems.length > 0 && (
                  <TableContainer component={Paper} style={{ marginTop: 20 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={styles.columnCell}>{tCommon('PLU')}</TableCell>
                          <TableCell className={styles.nameColumn}>{tCommon('Item Name')}</TableCell>
                          <TableCell className={styles.columnCell}>{tCommon('Quantity')}</TableCell>
                          <TableCell className={styles.columnCell}>{tCommon('Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transferItems.map((row, index) => {
                          const isCombo = row.item?.type === 'COMBO';
                          return (
                            <React.Fragment key={index}>
                              <TableRow>
                                <TableCell>{row.plu || '-'}</TableCell>
                                <TableCell className={styles.nameColumn}>
                                  {row.name}
                                  {row.batch_data && row.batch_data.length > 0 && (
                                    <div className={styles.batchInfoContainer}>
                                      {row.batch_data.map((sb) => {
                                        const batch = (localBatches || []).find(
                                          (b) => String(b.id) === String(sb.batch_id)
                                        );
                                        return (
                                          <span key={sb.batch_id} className={styles.batchChip}>
                                            {batch?.batch_name || batch?.batch_no || sb.batch_id}:{' '}
                                            <strong>{sb.quantity}</strong>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.item?.is_batch_enabled ? (
                                    <NumberField
                                      type="text"
                                      value={row.quantity === 0 ? '' : row.quantity}
                                      style={{ cursor: 'pointer' }}
                                      sx={{ '& input': { cursor: 'pointer' } }}
                                      inputProps={{ readOnly: true }}
                                      onKeyDown={(e) => e.preventDefault()}
                                      InputProps={{
                                        onClick: (e) => {
                                          setEditingTableItemIndex(index);
                                          setBatchPopupAnchor(e.currentTarget);
                                        },
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <MuiIconButton color="primary" size="small">
                                              <ArrowDropDownIcon fontSize="small" />
                                            </MuiIconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  ) : (
                                    <NumberField value={row.quantity === 0 ? '' : row.quantity} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isCombo && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setTransferItems(
                                          transferItems.map((ti, i) =>
                                            i === index ? { ...ti, showSubItems: !ti.showSubItems } : ti
                                          )
                                        );
                                      }}
                                    >
                                      {row.showSubItems ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  )}
                                  <IconButton onClick={() => dltItem(row)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              {isCombo && row.showSubItems && (
                                <TableRow>
                                  <TableCell colSpan={4} style={{ paddingLeft: 40, border: 'none' }}>
                                    <Collapse in={row.showSubItems} timeout="auto" unmountOnExit>
                                      <Box className={styles.subItemsContainer}>
                                        <Typography
                                          variant="h6"
                                          gutterBottom
                                          component="div"
                                          style={{ fontSize: 14, fontWeight: 600, paddingTop: 6 }}
                                        >
                                          {tCommon('Sub Items')}
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell className={styles.subTableHeadCell}>
                                                {tCommon('PLU')}
                                              </TableCell>
                                              <TableCell className={styles.subTableHeadCell}>
                                                {tCommon('Item Name')}
                                              </TableCell>
                                              <TableCell className={styles.subTableHeadCell}>
                                                {tCommon('Quantity')}
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(row.sub_item || []).map((subRow, subIndex) => (
                                              <TableRow key={subIndex}>
                                                <TableCell>{subRow.plu || '-'}</TableCell>
                                                <TableCell>
                                                  {subRow.name}
                                                  {subRow.batch_data && subRow.batch_data.length > 0 && (
                                                    <div className={styles.batchInfoContainer}>
                                                      {subRow.batch_data.map((sb) => {
                                                        const batch = (localBatches || []).find(
                                                          (b) => String(b.id) === String(sb.batch_id)
                                                        );
                                                        return (
                                                          <span key={sb.batch_id} className={styles.batchChip}>
                                                            {batch?.batch_name || batch?.batch_no || sb.batch_id}:{' '}
                                                            <strong>{sb.quantity}</strong>
                                                          </span>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {subRow.is_batch_enabled ? (
                                                    <NumberField
                                                      value={subRow.quantity === 0 ? '' : subRow.quantity}
                                                      style={{ minWidth: '100px', cursor: 'pointer' }}
                                                      sx={{ '& input': { cursor: 'pointer' } }}
                                                      inputProps={{ readOnly: true }}
                                                      onKeyDown={(e) => e.preventDefault()}
                                                      InputProps={{
                                                        onClick: (e) => {
                                                          handleOpenSubItemBatchPopup(e, index, subRow);
                                                        },
                                                        endAdornment: (
                                                          <InputAdornment position="end">
                                                            <MuiIconButton color="primary" size="small">
                                                              <ArrowDropDownIcon fontSize="small" />
                                                            </MuiIconButton>
                                                          </InputAdornment>
                                                        ),
                                                      }}
                                                    />
                                                  ) : (
                                                    <NumberField value={subRow.quantity === 0 ? '' : subRow.quantity} />
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Grid container spacing={3} style={{ marginTop: 20 }}>
                  <Grid item xs={12}>
                    <TextField
                      label={tCommon('Remark')}
                      multiline
                      rows={2}
                      variant="outlined"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>

                <Grid container className={styles.submitSection} display="flex" justifyContent="flex-end">
                  {transferId ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      className={styles.draftBtn}
                      onClick={() => history.push(ROUTES.INTERNAL_STOCK_TRANSFER)}
                    >
                      {tCommon('Back')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      className={styles.draftBtn}
                      onClick={() => history.push(ROUTES.INTERNAL_STOCK_TRANSFER)}
                    >
                      {tCommon('Cancel')}
                    </Button>
                  )}

                  {transferId && editData?.status === TRANSFER_STATUS.DRAFT && (
                    <Button
                      variant="outlined"
                      color="error"
                      style={{ marginLeft: 5 }}
                      className={styles.draftBtn}
                      disabled={loadingIndicator}
                      onClick={handleDeleteStockTransfer}
                    >
                      {tCommon('Delete')}
                    </Button>
                  )}

                  {(!transferId || editData?.status === TRANSFER_STATUS.DRAFT) && (
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ marginLeft: 5 }}
                      className={styles.draftBtn}
                      disabled={loadingIndicator}
                      onClick={() => {
                        if (transferId) {
                          handleupdateStockTransfer(TRANSFER_STATUS.DRAFT);
                        } else {
                          handleAddStockTransfer(TRANSFER_STATUS.DRAFT);
                        }
                      }}
                    >
                      {transferId ? tCommon('Update as draft') : tCommon('Save as Draft')}
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginLeft: 5 }}
                    className={styles.draftBtn}
                    disabled={loadingIndicator}
                    onClick={() => {
                      if (transferId) {
                        handleupdateStockTransfer(TRANSFER_STATUS.TRANSIT);
                      } else {
                        handleAddStockTransfer(TRANSFER_STATUS.TRANSIT);
                      }
                    }}
                  >
                    {transferId ? tCommon('Update as transit') : tCommon('Send')}
                  </Button>
                </Grid>
              </>
            )}
          </div>
        </div>
      </div>

      <BatchSelectionPopup
        open={subItemBatchPopup.open}
        anchorEl={subItemBatchPopup.anchorEl}
        onClose={() => setSubItemBatchPopup({ ...subItemBatchPopup, open: false })}
        item={subItemBatchPopup.item}
        stockBatches={filteredStockBatches}
        selectedBatches={subItemBatchPopup.subItem?.batch_data || []}
        originalBatchQtys={originalBatchQtys}
        onChange={handleSubItemBatchChange}
        showExpired={false}
      />
      <BatchSelectionPopup
        open={Boolean(batchPopupAnchor)}
        anchorEl={batchPopupAnchor}
        onClose={() => {
          setBatchPopupAnchor(null);
          setEditingTableItemIndex(null);
        }}
        item={editingTableItemIndex !== null ? transferItems[editingTableItemIndex]?.item : item}
        stockBatches={filteredStockBatches}
        selectedBatches={
          editingTableItemIndex !== null ? transferItems[editingTableItemIndex]?.batch_data || [] : selectedItemBatches
        }
        originalBatchQtys={originalBatchQtys}
        onChange={handleBatchQuantityChange}
        showExpired={false}
      />
    </>
  );
}

export default withConsoleBase(NewInternalStockTransfer);
